/* eslint-disable @typescript-eslint/restrict-plus-operands */
import { ColumnDef } from "@tanstack/react-table";
import { FC, useMemo } from "react";
import styled from "styled-components";
import {
  ReactTableComponent,
  Typography,
  TypographyVariant,
} from "../../../../../components/ui-kit";
import { LockGrey } from "../../../../../components/icons";
import {
  CurrencyCodes,
  Note,
  OrderDeliveryType,
  OrderProductPriceType,
  OrderStatus,
  PaymentStatus,
} from "@shared_packages/toolkit/types";
import { OrderApiType, OrderProductApiType } from "../../../../../util/types";
import { DescriptionColumn } from "./DescriptionColumn";
import {
  calculateOrderProductPrice,
  calculateOrderProductsAmounts,
  formatCurrency,
} from "../../../../../util/data-utils";

const ColumnMainContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const TableContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 20px 30px 30px;
  @media (max-width: 767px) {
    padding: 14px;
  }
  background: #ffffff;
  border: 1px solid #eeeeee;
  border-radius: 10px;
  height: max-content;
  @media (max-width: 900px) {
    padding: 20px 10px;
  }
`;

const NotesContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 20px 30px 30px;
  @media (max-width: 767px) {
    padding: 14px;
  }
  background: #ffffff;
  border: 1px solid #eeeeee;
  border-radius: 10px;
  height: max-content;
  @media (max-width: 900px) {
    padding: 10px;
  }
`;

const StyledReactTableComponent = styled(ReactTableComponent)<{
  hasRefund: boolean;
}>`
  thead {
    tr {
      th {
        text-align: right;
        :first-child {
          text-align: left;
          max-width: calc(300px - 15px * 2);
        }
        :last-child {
          ${({ hasRefund }) => hasRefund && "text-align: left;"}
        }
      }
    }
  }
  tbody {
    tr {
      td {
        :first-child {
          text-align: left;
          max-width: calc(300px - 15px * 2);
        }
        text-align: right;
      }
    }
  }
`;

const TopLine = styled.div`
  display: flex;
  margin-bottom: 15px;
`;

const Label = styled(Typography)`
  line-height: 20px;
  margin-right: 12px;
`;

const SourceSpan = styled.span`
  color: #027aff;
  margin-left: 7px;
`;

const UnderTopLine = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
`;

const Cell = styled.div`
  padding: 0 15px;
`;

const CalculationWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  border-top: 1px solid #eeeeee;
`;

const Calculation = styled.div`
  min-width: 290px;
  padding-top: 13px;
  display: flex;
  flex-direction: column;
`;

const RefundCalculation = styled.div`
  display: flex;
  flex-direction: column;
  min-width: max-content;
`;

const CalcLine = styled.div`
  display: flex;
  justify-content: space-between;
`;

const NotesTopLine = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 3px;
`;

const Footnote = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  padding-bottom: 12px;
  border-bottom: 1px solid #eeeeee;
`;

const NotesList = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 20px;
`;

const RefundWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  margin-top: 16px;
  border-top: 1px solid #eeeeee;
  padding-top: 16px;
  gap: 16px;
`;

const ColMiddleWrapper = styled.div`
  width: 50%;
`;

const BoxAmount = styled.div<{ hasRefund?: boolean }>`
  display: flex;
  justify-content: ${({ hasRefund }) =>
    hasRefund ? "space-between" : "flex-end"};
  align-items: center;
  gap: 20px;
`;

const RefundBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const RefundTypo = styled.div`
  display: flex;
  flex-direction: column;
  min-width: max-content;
  margin-right: 50px;
`;

interface DetailsTableProps {
  data: OrderApiType;
}

export const OrderDetailsTableWithNotes: FC<DetailsTableProps> = ({ data }) => {
  const { products } = data;

  const currentCurrency = data.currency ?? CurrencyCodes.POUND_STERLING;
  const isWholesale =
    data.productsPriceType === OrderProductPriceType.WHOLESALE;

  // const {
  //   total,
  //   retailPriceIncVat,
  //   totalDiscountAmountIncVat,
  //   totalVatAmount,
  //   totalShippingAmount,
  //   totalRefundAmount,
  // } = useMemo(() => {
  //   return calcOrderAmounts(products, data.discount, data.shippingCost);
  // }, [products]);

  // Calculation
  const {
    total,
    subtotalAmountIncVatDiscountShipping: subtotalAmountIncVat,
    discountAmount,
    vatAmount,
    shippingAmount,
    totalRefundAmount,
  } = useMemo(() => {
    return calculateOrderProductsAmounts(
      products.map((pr) => ({
        ...pr,
        currentCurrency,
      })),
      data?.discount,
      data?.shippingCost,
      data?.customFields,
      false,
      true
    );
  }, [products]);
  // END Calculation

  const filteredNotes = data?.notes
    ?.filter((note) => note.deletedAt === null)
    .sort(
      (a, b) =>
        new Date(b.updatedAt ?? b.createdAt).getTime() -
        new Date(a.updatedAt ?? a.createdAt).getTime()
    );

  const columns: Array<ColumnDef<OrderProductApiType, any>> = [
    {
      header: "Description",
      accessorKey: "description",
      cell: ({ row: { original } }) => {
        const optionConfig = original.productVariant?.productOptionConfig ?? [];
        const defaultVariant =
          optionConfig.length === 1 &&
          optionConfig[0].optionValue === "Default";
        return (
          <DescriptionColumn
            image={original.imgUrl ?? original.productVariant?.images?.[0]?.url}
            productName={original.product?.name ?? original.name ?? ""}
            productVariantName={
              !defaultVariant && optionConfig.length > 0
                ? optionConfig.map((config) => config.optionValue).join(" / ")
                : undefined
            }
          />
        );
      },
    },
    {
      header: "Unit cost exc. VAT",
      accessorKey: "amount",
      cell: ({ row: { original } }) => {
        return (
          <Cell>
            {formatCurrency(
              original.amount ??
                (isWholesale
                  ? original.productVariant?.pricing?.[currentCurrency]
                      ?.wholesaleTiers?.[0]?.price
                  : original.productVariant?.pricing?.[currentCurrency]?.price),
              2,
              currentCurrency
            )}
          </Cell>
        );
      },
    },
    {
      header: "VAT %",
      accessorKey: "vat",
      cell: ({ row: { original } }) => (
        <Cell>{original.vatRate?.toFixed(0) ?? 0}</Cell>
      ),
    },
    {
      header: "Units",
      accessorKey: "units",
      cell: ({ row: { original } }) => {
        return <Cell>{original.qty}</Cell>;
      },
    },
    {
      header: "Amount",
      accessorKey: "amount",
      cell: ({ row: { original } }) => {
        return (
          <Cell>
            <BoxAmount
              hasRefund={
                data.status === OrderStatus.REFUNDED ||
                data.payment?.status === PaymentStatus.REFUNDED ||
                data.payment?.status === PaymentStatus.PARTIAL_REFUND
              }
            >
              <Typography
                style={
                  data.status === OrderStatus.REFUNDED
                    ? {
                        marginRight: "-30px",
                      }
                    : undefined
                }
                variant={TypographyVariant.BODY3}
              >
                {formatCurrency(
                  calculateOrderProductPrice(
                    { ...original, currentCurrency },
                    false,
                    true
                  ).priceIncVatDiscountShipping,
                  2,
                  currentCurrency
                )}
              </Typography>
              {original.refundedQty &&
              original.refundedQty > 0 &&
              original.qty === 0 ? (
                <Typography color="#EF6355" variant={TypographyVariant.BODY6}>
                  Refunded
                </Typography>
              ) : original.refundedQty &&
                original.refundedQty > 0 &&
                original.qty !== 0 ? (
                <RefundBox>
                  <Typography color="#EF6355" variant={TypographyVariant.BODY6}>
                    Partially refunded
                  </Typography>
                  <Typography color="#EF6355" variant={TypographyVariant.BODY6}>
                    ({original.refundedQty} out of{" "}
                    {original.qty + original.refundedQty} products)
                  </Typography>
                </RefundBox>
              ) : null}
            </BoxAmount>
          </Cell>
        );
      },
    },
  ];

  return (
    <ColumnMainContainer>
      <TableContainer>
        <TopLine>
          <Label variant={TypographyVariant.BODY3}>
            Source:
            <SourceSpan>
              {data.deliveryType === OrderDeliveryType.IN_PERSON
                ? "In person"
                : "Delivery / pick-up"}
            </SourceSpan>
          </Label>
          <Label variant={TypographyVariant.BODY3}>
            Seller:
            <SourceSpan>
              {data.createdBy?.firstName} {data.createdBy?.lastName}
            </SourceSpan>
          </Label>
        </TopLine>
        <UnderTopLine>
          <Typography
            style={{ lineHeight: "34px" }}
            variant={TypographyVariant.HEADER2}
          >
            Items
          </Typography>
        </UnderTopLine>
        <StyledReactTableComponent
          // editMode={editMode}
          hasRefund={
            data.status === OrderStatus.REFUNDED ||
            data.payment?.status === PaymentStatus.REFUNDED ||
            data.payment?.status === PaymentStatus.PARTIAL_REFUND
          }
          padding="0"
          columns={columns}
          data={products}
        />
        <CalculationWrapper>
          <Calculation>
            <CalcLine>
              <Typography variant={TypographyVariant.HEADLINE}>
                Subtotal
              </Typography>
              <Typography variant={TypographyVariant.HEADLINE}>
                {formatCurrency(subtotalAmountIncVat, 2, currentCurrency)}
              </Typography>
            </CalcLine>

            <CalcLine>
              <Typography variant={TypographyVariant.HEADLINE}>
                Discount
              </Typography>
              <Typography variant={TypographyVariant.HEADLINE}>
                {formatCurrency(discountAmount, 2, currentCurrency)}
              </Typography>
            </CalcLine>
            <CalcLine>
              <Typography variant={TypographyVariant.HEADLINE}>VAT</Typography>
              <Typography variant={TypographyVariant.HEADLINE}>
                {formatCurrency(vatAmount, 2, currentCurrency)}
              </Typography>
            </CalcLine>
            {data.deliveryType !== OrderDeliveryType.IN_PERSON ? (
              <CalcLine
                style={{
                  paddingBottom: "13px",
                  borderBottom: "1px solid #eeeeee",
                  marginBottom: "13px",
                }}
              >
                <Typography variant={TypographyVariant.HEADLINE}>
                  Shipping
                </Typography>
                <Typography variant={TypographyVariant.HEADLINE}>
                  {formatCurrency(shippingAmount, 2, currentCurrency)}
                </Typography>
              </CalcLine>
            ) : null}
            <CalcLine>
              <Typography variant={TypographyVariant.HEADER3}>Total</Typography>
              <Typography variant={TypographyVariant.HEADER3}>
                {formatCurrency(total, 2, currentCurrency)}
              </Typography>
            </CalcLine>
          </Calculation>
        </CalculationWrapper>
        {(data.payment?.status === PaymentStatus.REFUNDED ||
          data.payment?.status === PaymentStatus.PARTIAL_REFUND) && (
          <RefundWrapper>
            <RefundTypo>
              <Typography variant={TypographyVariant.BODY3}>
                {products.filter((pr) => pr.qty === 0).length ===
                products.length
                  ? "Refunded"
                  : "Partially refunded"}
              </Typography>
              <Typography variant={TypographyVariant.HEADER3}>
                Net payment
              </Typography>
            </RefundTypo>
            <ColMiddleWrapper>
              <Typography variant={TypographyVariant.BODY3}>
                {(data.payment.notes.find((note: Note) =>
                  note.text.includes("r:")
                ) &&
                  `Reason: “${data.payment?.notes
                    .find((note: Note) => note.text.includes("r:"))
                    .text.slice(2)}”`) ??
                  null}
              </Typography>
            </ColMiddleWrapper>
            <RefundCalculation>
              <Typography
                variant={TypographyVariant.HEADLINE}
                style={{ textAlign: "right" }}
              >
                -{formatCurrency(totalRefundAmount, 2, currentCurrency)}
              </Typography>

              <Typography
                variant={TypographyVariant.HEADER3}
                style={{ textAlign: "right" }}
              >
                {formatCurrency(total - totalRefundAmount, 2, currentCurrency)}
              </Typography>
            </RefundCalculation>
          </RefundWrapper>
        )}
      </TableContainer>

      <NotesContainer>
        <NotesTopLine>
          <Typography variant={TypographyVariant.HEADER2}>Notes</Typography>
        </NotesTopLine>
        <Footnote>
          <LockGrey />
          <Typography color="#6C6C8A" variant={TypographyVariant.BODY7}>
            Order notes are not visible for customers
          </Typography>
        </Footnote>
        <NotesList>
          {filteredNotes?.length ? (
            filteredNotes?.map((el) => {
              return el.text;
            })
          ) : (
            <Typography variant={TypographyVariant.BODY3}>
              There are no notes for this order
            </Typography>
          )}
        </NotesList>
      </NotesContainer>
    </ColumnMainContainer>
  );
};
